/**
 * Aba "DinaBox" (Conta) — credencial PESSOAL do usuario na API DinaBox.
 *
 * Self-service: cada usuario cadastra o proprio usuario + senha do DinaBox, que
 * o backend usa como proxy para o Revisor de Lista de Ferragens. A senha e
 * write-only (cifrada Fernet no backend, nunca volta para o navegador).
 *
 * O login do DinaBox e um codigo (ex.: 5 digitos), nao um e-mail.
 *
 * Backend: /api/me/dinabox-credencial (GET/PUT/DELETE) + .../testar (POST).
 */
import React, { useCallback, useEffect, useState } from "react";

import AsyncButton from "../../../components/AsyncButton";
import PasswordInput from "../../../components/PasswordInput";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";

function formatarData(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}

export default function IntegracaoDinaBoxTab() {
  const { request } = useAuth();
  const toast = useToast();

  const [cred, setCred] = useState(null);
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [testeMsg, setTesteMsg] = useState(null); // {ok, mensagem}

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const p = await request("/api/me/dinabox-credencial", { silentError: true });
      setCred(p);
      setUsuario(p?.usuario_dinabox || "");
    } catch {
      setCred({ usuario_dinabox: "", senha_configurada: false });
    } finally {
      setCarregando(false);
    }
  }, [request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const testar = async () => {
    setTesteMsg(null);
    try {
      const p = await request("/api/me/dinabox-credencial/testar", {
        method: "POST",
        silentError: true,
      });
      setTesteMsg({ ok: true, mensagem: p?.mensagem || "Conexão validada." });
      toast.success(p?.mensagem || "Conexão com o DinaBox validada.");
      carregar();
    } catch (err) {
      // mensagem amigavel vinda do backend (categoria credencial/servico);
      // NUNCA mostra erro tecnico cru.
      const msg =
        err?.payload?.mensagem ||
        "Não foi possível validar a conexão com o DinaBox. Tente novamente.";
      setTesteMsg({ ok: false, mensagem: msg });
      toast.error(msg);
    }
  };

  const salvar = async () => {
    const body = { usuario_dinabox: usuario.trim() };
    if (senha) body.senha = senha;
    const p = await request("/api/me/dinabox-credencial", {
      method: "PUT",
      body: JSON.stringify(body),
    });
    setCred(p);
    setSenha("");
    setTesteMsg(null);
    // Valida automaticamente contra o DinaBox e mostra o resultado (credencial
    // incorreta vs serviço indisponível) como notificação amigável.
    await testar();
  };

  const remover = async () => {
    await request("/api/me/dinabox-credencial", { method: "DELETE", silentError: true });
    setCred({ usuario_dinabox: "", senha_configurada: false });
    setUsuario("");
    setSenha("");
    setTesteMsg(null);
  };

  const configurada = Boolean(cred?.senha_configurada);

  return (
    <section className="set-section">
      <div className="set-section__head">
        <h2 className="set-section__title">DinaBox</h2>
        <p className="set-section__desc">
          Cadastre seu usuário e senha do DinaBox. São usados pelo Revisor de
          Lista de Ferragens para buscar seus projetos. A senha é guardada
          cifrada e nunca é exibida de volta.
        </p>
      </div>

      <div className="set-section__body">
        {/* Status */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Status</div>
            <div className="set-row__desc">Situação da sua conexão com o DinaBox.</div>
          </div>
          <div className="set-row__control">
            {carregando ? (
              <span style={{ color: "var(--ink-3)", fontSize: 13 }}>Carregando…</span>
            ) : configurada ? (
              <span
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  color: "var(--success-text)", fontSize: 13, fontWeight: 500,
                }}
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                Credencial configurada
                {cred?.ultima_validacao_em ? (
                  <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>
                    · validada em {formatarData(cred.ultima_validacao_em)}
                  </span>
                ) : null}
              </span>
            ) : (
              <span
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  color: "var(--warning-text)", fontSize: 13, fontWeight: 500,
                }}
              >
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                Pendente de configuração
              </span>
            )}
          </div>
        </div>

        {/* Credencial */}
        <div className="set-row">
          <div>
            <div className="set-row__lbl">Credenciais DinaBox</div>
            <div className="set-row__desc">
              O mesmo usuário e senha que você usa em dinabox.app.
            </div>
          </div>
          <div className="set-row__control">
            <div className="set-fields">
              <div className="input-group col-2">
                <label className="input-label">Usuário DinaBox</label>
                <input
                  className="input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="12345"
                  value={usuario}
                  onChange={(e) => setUsuario(e.target.value)}
                />
              </div>
              <div className="input-group col-2">
                <label className="input-label">Senha</label>
                <PasswordInput
                  autoComplete="new-password"
                  placeholder={configurada ? "•••••••• (deixe vazio para não alterar)" : "Sua senha do DinaBox"}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                />
              </div>
            </div>

            {testeMsg ? (
              <div
                style={{
                  marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: 13,
                  background: testeMsg.ok ? "var(--success-bg)" : "var(--danger-bg)",
                  color: testeMsg.ok ? "var(--success-text)" : "var(--danger-text)",
                  border: `1px solid ${testeMsg.ok ? "var(--success-border)" : "var(--danger-border)"}`,
                }}
              >
                {testeMsg.mensagem}
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12, flexWrap: "wrap" }}>
              {configurada ? (
                <AsyncButton
                  variant="secondary"
                  onClick={remover}
                  successMessage="Credencial removida."
                >
                  Remover
                </AsyncButton>
              ) : null}
              {configurada ? (
                <AsyncButton variant="secondary" onClick={testar} silentSuccess silentError>
                  Testar conexão
                </AsyncButton>
              ) : null}
              <AsyncButton
                variant="primary"
                onClick={salvar}
                silentSuccess
                disabled={!usuario.trim() || (!configurada && !senha)}
              >
                Salvar
              </AsyncButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
